import type { Lead } from '@/shared/types'

// A lead added by hand or from an imported list starts at 100 until an AI
// verdict replaces it (YCRM-41). It has no AI brief, so the detail view hides
// those sections.
const START_SCORE_LABELS = { manual: 'Manuell', import: 'Importerad' } as const

/** "Manuell" or "Importerad" for a start score, null for an AI score or none. */
export function startScoreLabel(lead: Pick<Lead, 'scoreSource'>): string | null {
  return lead.scoreSource === 'manual' || lead.scoreSource === 'import'
    ? START_SCORE_LABELS[lead.scoreSource]
    : null
}

export function isStartScore(lead: Pick<Lead, 'scoreSource'>): boolean {
  return startScoreLabel(lead) != null
}

/** "100 · Manuell" / "100 · Importerad" for a start score, "85" for an AI one,
 *  null for none. */
export function scoreText(lead: Pick<Lead, 'score' | 'scoreSource'>): string | null {
  if (lead.score == null) return null
  const label = startScoreLabel(lead)
  return label ? `${lead.score} · ${label}` : String(lead.score)
}

/** The score to rank by, or null for a lead shown as "Ej bedömt". The API
 *  already sends null then; the flag is checked too so a lead that carries a
 *  number it marks unavailable can never rank above a real score. */
export function sortableScore(lead: Pick<Lead, 'score' | 'scoreAvailable'>): number | null {
  return lead.scoreAvailable === false ? null : lead.score
}

/** The AI score's evidence (verdict confidence) 0-100, shown "Underlag 60/100",
 *  or null: without one, for a start score, or without an AI score. */
export function underlag(lead: Pick<Lead, 'confidence' | 'score' | 'scoreAvailable' | 'scoreSource'>): number | null {
  const c = lead.confidence
  if (c == null || !Number.isFinite(c) || c < 0 || c > 100) return null
  if (sortableScore(lead) == null || isStartScore(lead)) return null
  return Math.round(c)
}
